import { useEffect, useMemo } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { useSessionContent } from './useSessionContent';
import { useSessionTranslation } from './useSessionTranslation';
import {
  replayContentLoaded,
  replayFailed,
  replayTranslated,
  replayTranslating,
} from '../redux/actions';
import { selectIsReplay, selectReplayLanguage, selectReplayPhase } from '../redux/selectors';
import { messagesToTurns } from '../utils/utils';

type SyncArgs = { sessionId: string | undefined; sessionStart?: string };

/**
 * The Apollo→Redux sync for replay mode. **Call once at the modal root.**
 *
 * Pulls session content from the analytics GraphQL, optionally fetches a
 * translation, parses both into `ConversationTurn[]`, and drives the replay
 * state machine via `replayContentLoaded` / `replayTranslating` /
 * `replayTranslated` / `replayFailed`.
 *
 * This is the load-bearing departure from the legacy `useLogs` design:
 * Apollo's cache stays as the network-layer source of truth (so multiple
 * panels asking for `useSessionContent(sameId)` still share one request),
 * but the *parsed* timeline lives in Redux. Every panel that needs the
 * timeline reads `selectMessages` — exactly like the test-mode path.
 */
export const useReplayMessagesSync = ({ sessionId, sessionStart }: SyncArgs) => {
  const dispatch = useDispatch();
  const isReplay = useSelector(selectIsReplay);
  const language = useSelector(selectReplayLanguage);
  const phase = useSelector(selectReplayPhase);

  const {
    data: content,
    isLoading: contentLoading,
    error: contentError,
  } = useSessionContent(sessionId, sessionStart);

  const {
    translated,
    isLoading: translating,
    isError: translationFailed,
  } = useSessionTranslation(sessionId, language);

  const originalTurns = useMemo(
    () => (content ? messagesToTurns(content.messages) : null),
    [content]
  );

  const translatedTurns = useMemo(
    () => (translated ? messagesToTurns(translated) : null),
    [translated]
  );

  // Drive the state machine. The slice ignores transitions that don't match
  // the current mode (so a stray dispatch during a quick close→reopen is safe).
  // A translation failure is non-critical: the caller surfaces it separately
  // and we leave the loaded original timeline in place.
  useEffect(() => {
    if (!isReplay) {
      return;
    }
    if (contentError) {
      dispatch(replayFailed());
      return;
    }
    if (contentLoading || !originalTurns) {
      return;
    }
    // Only dispatch the initial-load transition while the machine is still in
    // `loading`. Apollo can hand back a fresh `content` reference on every
    // cache write — re-dispatching here would wipe `state.messages` on every
    // background refetch and cause every downstream selector to recompute.
    if (phase !== 'loading') {
      return;
    }
    dispatch(replayContentLoaded({ turns: originalTurns, brainId: content?.brain_parent_id }));
  }, [isReplay, contentLoading, contentError, originalTurns, phase, content, dispatch]);

  useEffect(() => {
    if (!isReplay) {
      return;
    }
    if (!language) {
      // Translation toggled off: restore original turns so the timeline is
      // not left showing stale translated content. Skipped while the initial
      // content load is still in flight (the first effect handles that).
      if (originalTurns && phase !== 'loading') {
        dispatch(replayTranslated({ turns: originalTurns }));
      }
      return;
    }
    if (translating) {
      dispatch(replayTranslating());
      return;
    }
    if (translationFailed && originalTurns) {
      dispatch(replayTranslated({ turns: originalTurns }));
      return;
    }
    if (translatedTurns) {
      dispatch(replayTranslated({ turns: translatedTurns }));
    }
  }, [
    isReplay,
    language,
    translating,
    translationFailed,
    translatedTurns,
    originalTurns,
    phase,
    dispatch,
  ]);
};
